/**
 * Two-stage connector action loading for the step editor.
 *
 * Stage 1 fetches only the names and labels the action dropdown renders; stage 2 fetches one
 * action's form definition once it is selected. The single-document endpoint this replaces returns
 * up to 14MB for a connector with 2330 actions, which the browser then parses into ~142k objects
 * and Vue wraps in reactive proxies — all so that a dropdown can show a list of labels.
 *
 * Both stages are cached in module scope, because the step editor is re-created on every step
 * click (its parent bumps `editStepKey`), so without a cache a flow with ten steps on the same
 * connector re-downloads the same payload ten times.
 *
 * The cache is deliberately not keyed by tenant or organization: connector specs live in the
 * master schema and are identical for every tenant, so there is nothing to invalidate when the
 * signed-in user or organization changes.
 */
import { apiCall, logResponse, Response } from "@/core/api";
import REQUEST_TYPE from "@/core/api/request-types";
import { getParameter } from "@/core/util";

export interface ActionSummary {
  actionSpec: {
    name: string;
    description?: string;
    actionType?: string;
    tooltip?: string;
  };
}

const ENDPOINT_PREFIX = "/connector-actions";

// Last-resort fallbacks, used only when the configured value is missing or unparseable. They must
// not depend on a fetch, so they are literals here and mirrored in .env.development/config.json.
const DEFAULT_SUMMARY_CACHE_SIZE = 8;
const DEFAULT_DETAIL_CACHE_SIZE = 50;
const DEFAULT_DROPDOWN_RENDER_LIMIT = 100;
// Grouping is only worth it when most actions carry a tag and the headers actually compress the
// list. Measured across the catalog: 2.3 (zendesk, not worth grouping) and 4.3 (orangelogic,
// worth it) sit either side of 3, and pimcore has no tags at all.
const DEFAULT_GROUPING_MIN_PREFIX_RATIO = 0.8;
const DEFAULT_GROUPING_MIN_PER_GROUP = 3;

const summaryCache = new Map<string, ActionSummary[]>();
const detailCache = new Map<string, any>();

let limitsPromise: Promise<{
  summaryCacheSize: number;
  detailCacheSize: number;
  dropdownRenderLimit: number;
  groupingMinPrefixRatio: number;
  groupingMinPerGroup: number;
}> | null = null;

function clampInt(raw: unknown, fallback: number, min: number, max: number) {
  const parsed = parseInt(String(raw ?? "").replace(/_/g, ""), 10);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  return Math.min(max, Math.max(min, parsed));
}

function clampFloat(raw: unknown, fallback: number, min: number, max: number) {
  const parsed = parseFloat(String(raw ?? ""));
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  return Math.min(max, Math.max(min, parsed));
}

async function getLimits() {
  if (!limitsPromise) {
    limitsPromise = (async () => {
      const [summary, detail, render, ratio, perGroup] = await Promise.all([
        getParameter("ACTION_SUMMARY_CACHE_SIZE"),
        getParameter("ACTION_DETAIL_CACHE_SIZE"),
        getParameter("ACTION_DROPDOWN_RENDER_LIMIT"),
        getParameter("ACTION_GROUPING_MIN_PREFIX_RATIO"),
        getParameter("ACTION_GROUPING_MIN_PER_GROUP"),
      ]);
      return {
        summaryCacheSize: clampInt(summary, DEFAULT_SUMMARY_CACHE_SIZE, 1, 100),
        detailCacheSize: clampInt(detail, DEFAULT_DETAIL_CACHE_SIZE, 1, 500),
        dropdownRenderLimit: clampInt(
          render,
          DEFAULT_DROPDOWN_RENDER_LIMIT,
          10,
          2000
        ),
        // A ratio of 1 demands every action be tagged; 0 would group anything, so the floor is
        // deliberately above it.
        groupingMinPrefixRatio: clampFloat(
          ratio,
          DEFAULT_GROUPING_MIN_PREFIX_RATIO,
          0.1,
          1
        ),
        groupingMinPerGroup: clampFloat(
          perGroup,
          DEFAULT_GROUPING_MIN_PER_GROUP,
          1,
          100
        ),
      };
    })().catch(() => ({
      summaryCacheSize: DEFAULT_SUMMARY_CACHE_SIZE,
      detailCacheSize: DEFAULT_DETAIL_CACHE_SIZE,
      dropdownRenderLimit: DEFAULT_DROPDOWN_RENDER_LIMIT,
      groupingMinPrefixRatio: DEFAULT_GROUPING_MIN_PREFIX_RATIO,
      groupingMinPerGroup: DEFAULT_GROUPING_MIN_PER_GROUP,
    }));
  }
  return limitsPromise;
}

export async function getActionDropdownRenderLimit(): Promise<number> {
  return (await getLimits()).dropdownRenderLimit;
}

/** Thresholds deciding whether grouping the action dropdown is worth it for a connector. */
export async function getActionGroupingThresholds(): Promise<{
  minPrefixRatio: number;
  minPerGroup: number;
}> {
  const limits = await getLimits();
  return {
    minPrefixRatio: limits.groupingMinPrefixRatio,
    minPerGroup: limits.groupingMinPerGroup,
  };
}

/** Insertion-ordered Map doubles as an LRU: re-set on read, drop the oldest key on overflow. */
function touch<T>(
  cache: Map<string, T>,
  key: string,
  value: T,
  maxSize: number
) {
  cache.delete(key);
  cache.set(key, value);
  while (cache.size > maxSize) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) {
      break;
    }
    cache.delete(oldest);
  }
}

function read<T>(cache: Map<string, T>, key: string): T | undefined {
  if (!cache.has(key)) {
    return undefined;
  }
  const value = cache.get(key) as T;
  cache.delete(key);
  cache.set(key, value);
  return value;
}

// Joined on NUL, written as an escape so this file stays plain text: it is the one character that
// cannot appear in a connector spec id, revision or action name, so no pair of distinct key parts
// can collide (action names legitimately contain "/", ":" and spaces).
function cacheKey(...parts: (string | number | undefined | null)[]) {
  return parts
    .map((part) => (part === undefined || part === null ? "" : part))
    .join("\u0000");
}

/**
 * `apiCall` turns an HTTP failure into a response, but a non-Axios throw still rejects — a
 * misconfigured REST_URL, or a request that produced no response at all. Neither loader can let
 * that escape: the step editor tells a failed load apart from an empty one by the undefined
 * return, and a rejection would instead surface as an unhandled error.
 */
async function requestOrUndefined(
  endpoint: string
): Promise<Response | undefined> {
  try {
    return await apiCall("GET", endpoint, undefined, REQUEST_TYPE.CONNECTOR);
  } catch (e) {
    console.error(`Connector action request failed for ${endpoint}`, e);
    return undefined;
  }
}

function buildQuery(
  params: Record<string, string | number | undefined | null>
) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && `${value}` !== "") {
      search.set(key, `${value}`);
    }
  });
  const query = search.toString();
  return query ? `?${query}` : "";
}

/**
 * Stage 1. Returns `[{ actionSpec: { name, description, actionType, tooltip } }]` — the same
 * nesting the full document uses, so the dropdown's search index and template consume it
 * unchanged.
 *
 * Returns undefined when the connector or revision is unknown (404) or the request failed, which
 * the caller must keep apart from an empty array: the step editor shows its connector-error
 * warning for the former and an empty dropdown for the latter.
 */
export async function getActionSummaries(
  connectorSpecId: string,
  revision?: string | number,
  actionType?: string
): Promise<ActionSummary[] | undefined> {
  const key = cacheKey(connectorSpecId, revision, actionType);
  const cached = read(summaryCache, key);
  if (cached) {
    return cached;
  }

  const endpoint = `${ENDPOINT_PREFIX}/${encodeURIComponent(
    connectorSpecId
  )}${buildQuery({ revision, actionType })}`;
  const response = await requestOrUndefined(endpoint);
  if (!response) {
    return undefined;
  }
  if (response.code === 200) {
    const summaries: ActionSummary[] = response.result?.actions ?? [];
    touch(summaryCache, key, summaries, (await getLimits()).summaryCacheSize);
    return summaries;
  }
  if (response.code !== 404) {
    logResponse(response);
  }
  return undefined;
}

/**
 * Stage 2. Returns one action's UI node, or undefined when the action is gone (404) or the
 * request failed — the caller clears its selection rather than rendering a stale form.
 */
export async function getActionDetail(
  connectorSpecId: string,
  revision: string | number | undefined,
  actionName: string
): Promise<any | undefined> {
  if (!actionName) {
    return undefined;
  }
  const key = cacheKey(connectorSpecId, revision, actionName);
  const cached = read(detailCache, key);
  if (cached) {
    return cached;
  }

  const endpoint = `${ENDPOINT_PREFIX}/${encodeURIComponent(
    connectorSpecId
  )}/detail${buildQuery({ revision, name: actionName })}`;
  const response = await requestOrUndefined(endpoint);
  if (!response) {
    return undefined;
  }
  if (response.code === 200 && response.result) {
    touch(
      detailCache,
      key,
      response.result,
      (await getLimits()).detailCacheSize
    );
    return response.result;
  }
  if (response.code !== 404) {
    logResponse(response);
  }
  return undefined;
}

/** Exposed for tests; production code has nothing to invalidate (see the file comment). */
export function clearConnectorActionCache(): void {
  summaryCache.clear();
  detailCache.clear();
  limitsPromise = null;
}
